// TES-1185 (TES-1180 points 1 and 4) — the care meeting's final step when no
// kompletteringar are needed: the positive decision is taken in the same
// step. These are the step's rules, pure so `node --test` can run them.
//
// A-02 2026-10-08: "Om inga kompletteringar krävs är behandlingen slutligt
// fastställd. … publicerar beslutet och patientbeskedet."
import type {
  ClinicalPhase,
  CompletionOutcome,
  CreateSubscriptionLinkRequest,
  MeetingDecision,
  MeetingPrescriptionOrder,
} from "@care-by-ella/contracts";

export interface MeetingDecisionInput {
  /** The Recept card's three required parts are filled in. */
  orderComplete: boolean;
  clinicalPhase: ClinicalPhase | null;
  /** The patient still has medical follow-up open (TES-736). */
  followUpRemaining: boolean;
  followUpConfirmed: boolean;
  emailVerified: boolean;
  plan: CreateSubscriptionLinkRequest | null;
  prescription: MeetingDecision["prescription"] | null;
}

/** What still stands in the way of publishing the decision, or null. */
export function meetingDecisionProblem(d: MeetingDecisionInput): string | null {
  const plan = meetingDecisionPlanProblem(d);
  if (plan) return plan;
  // TES-1315 — the e-recept is its own Webdoc tick on the direct approval.
  if (!d.prescription) return "Bekräfta att e-receptet är utfärdat i Webdoc.";
  return null;
}

/**
 * TES-1316 — the part of the decision asked in step 4 ("Recept och
 * patienttexter", Ö-456): everything but the e-recept, which is step 5's
 * Webdoc tick.
 */
export function meetingDecisionPlanProblem(
  d: Omit<MeetingDecisionInput, "prescription">,
): string | null {
  if (!d.orderComplete)
    return "Fyll i läkemedel, dos och doseringsintervall under Recept.";
  if (!d.clinicalPhase) return "Välj klinisk behandlingsfas.";
  if (d.followUpRemaining && !d.followUpConfirmed)
    return "Bekräfta att uppföljningen får vara kvar.";
  if (!d.emailVerified)
    return "Patienten saknar verifierad e-postadress. Abonnemangslänken skickas bara via e-post.";
  if (!d.plan) return "Välj abonnemangslänk.";
  return null;
}

// TES-1314 (A-02 2026-10-13, step 2) — the outcome is chosen before Recept:
//   "Läkaren ska inte behöva fylla i recept, kommentar till
//   behandlingsbeslut eller irrelevanta Webdoc-bekräftelser innan utfallet
//   har valts."
// Recept is shown for an approval and for a komplettering (as the planned,
// conditional treatment), never before an outcome and never for "Kan inte gå
// vidare".
export function meetingShowsRecept(outcome: CompletionOutcome | null): boolean {
  return outcome === "proceed" || outcome === "completion";
}

/**
 * The order the meeting's journal states. While Recept is shown it is what
 * the fields hold. Otherwise no order is sent, and the API keeps the stored
 * one as it is, so the note states that one (null on a first documentation)
 * rather than fields the doctor can no longer see.
 */
export function meetingNoteOrder(
  outcome: CompletionOutcome | null,
  fields: MeetingPrescriptionOrder,
  stored: MeetingPrescriptionOrder | null | undefined,
): MeetingPrescriptionOrder | null {
  return meetingShowsRecept(outcome) ? fields : (stored ?? null);
}
